import React, { useEffect, useMemo } from 'react';
import { Icon, Notice } from '@heartinternet/hi-ui';
import { Link } from '@heartinternet/hi-ui';
import { Text } from '@heartinternet/hi-ui';
import { Tabs } from '@heartinternet/hi-ui';
import { SimpleProductCard } from './components/SimpleProductCard';
import { BusinessEmailCard } from './components/BusinessEmailCard';
import { ExchangeMailboxCard } from './components/ExchangeMailboxCard';
import { PremiumMailboxCard, DomainMailboxes } from './components/PremiumMailboxCard';
import type { ExtrasPageProps, Product } from './types';
import './ExtrasPage.css';

export type { ExtrasPageProps, Product, DomainMailboxes };

export interface ExtrasPageFullProps extends ExtrasPageProps {
  fetchMailboxes?: () => Promise<DomainMailboxes[]>;
}

const renderCard = (product: Product, props: ExtrasPageFullProps) => {
  if (product.tiers && product.tiers.length > 0) {
    return (
      <BusinessEmailCard
        product={product}
        formAction={props.formAction}
        isReseller={props.isReseller}
      />
    );
  }

  if (product.requiresEmail) {
    return (
      <ExchangeMailboxCard
        product={product}
        domains={props.domains}
        formAction={props.formAction}
        isReseller={props.isReseller}
      />
    );
  }

  if (product.requiresMailbox) {
    return (
      <PremiumMailboxCard
        product={product}
        formAction={props.formAction}
        isReseller={props.isReseller}
        fetchMailboxes={props.fetchMailboxes || (() => Promise.resolve([]))}
      />
    );
  }

  return (
    <SimpleProductCard
      product={product}
      formAction={props.formAction}
      isReseller={props.isReseller}
    />
  );
};

export const ExtrasPage: React.FC<ExtrasPageFullProps> = (props) => {
  const { products, successMessage, errors } = props;

  // Handle anchor scrolling — switch to matching tab via hash
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      const timer = setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  const defaultTab = useMemo(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      const match = products.find((p) => p.id === hash);
      if (match) return match.id;
    }
    return products[0].id;
  }, [products]);

  const tabs = useMemo(
    () =>
      products.map((product) => ({
        id: product.id,
        label: product.name,
        icon: product.iconName ? <Icon name={product.iconName} /> : undefined,
        content: (
          <div id={product.id} className="hi-ui-extras-page__product">
            {renderCard(product, props)}
          </div>
        ),
      })),
    [products, props]
  );

  return (
    <div id="hi-ui-page-root" className="hi-ui-extras-page">
      <Text variant="heading-1">Add-ons</Text>

      {successMessage && (
        <Notice variant="success" dismissible>
          {successMessage} has been added to your shopping basket.{' '}
          <Link href="/manage/basket.cgi">Checkout now</Link> or choose another add-on below.
        </Notice>
      )}

      {errors && errors.length > 0 && (
        <Notice variant="error">
          {errors.map((error, i) => (
            <div key={i}>{error}</div>
          ))}
        </Notice>
      )}

      <Tabs defaultTab={defaultTab} tabs={tabs} mobileIcons aria-label="Add-on products" />
    </div>
  );
};
